// newsCache.js - in-app LRU cache for news responses so navigating to the
// next ticker shows its news instantly instead of re-fetching.
//
// - Keyed by ticker id ONLY, so a response cached under one ticker is never
//   returned for another - this is what prevents wrong-ticker news from ever
//   showing (the browser HTTP cache alone can trip over this).
// - NewsPanel reads through this cache: if a prefetch already stored the
//   next ticker's news, the panel gets it immediately.
// - Keep-last behaviour: until the requested ticker's data is ready, the
//   previous ticker's news stays visible (no "Loading…" flash on navigation).

const MAX_ENTRIES = 60;
const cache = new Map();   // key: ticker id -> news array
const inflight = new Map(); // key: ticker id -> Promise<news array>

export function peekNews(id) {
  return cache.get(id) || null;
}

function store(id, news) {
  cache.set(id, news);
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined || oldest === id) break;
    cache.delete(oldest);
  }
}

export function fetchNews(id, apiFetch) {
  const hit = cache.get(id);
  if (hit) {
    cache.delete(id);
    cache.set(id, hit);
    return Promise.resolve(hit);
  }
  if (inflight.has(id)) return inflight.get(id);

  const promise = apiFetch(`/stock/${encodeURIComponent(id)}/news`)
    .then((r) => (r.ok ? r.json() : []))
    .then((rows) => {
      const list = Array.isArray(rows) ? rows : [];
      store(id, list);
      inflight.delete(id);
      return list;
    })
    .catch((err) => {
      inflight.delete(id);
      throw err;
    });
  inflight.set(id, promise);
  return promise;
}

export function prefetchNews(id, apiFetch) {
  if (!id) return;
  fetchNews(id, apiFetch).catch(() => {});
}
